import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useHideMobileNav from '../utils/useHideMobileNav';

// Category-aware processing messages (dry, understated humor)
const CATEGORY_MESSAGES = {
  liquor: "As an AI, I'm not much of a drinker—this may take a moment.",
  italian: "My Italian is a bit rusty—working through this carefully.",
  french: "French cuisine can be… detailed. Give me a moment.",
  food_general: "Reviewing your items—this shouldn't take long.",
  mixed: "Quite the variety here—processing now.",
};

// Rotating "jazz" status line. The AI accepts a whole document and says
// nothing until its first bulk response, so the real status can sit unchanged
// for a minute or more and the modal looks hung. Whenever the real status has
// gone quiet, we rotate plausible process descriptions instead; any fresh
// statusText prop takes precedence and resets the quiet timer.
const JAZZ = [
  "Sending your items to the AI…",
  "Reading every line carefully…",
  "Matching items to your vendors…",
  "Comparing pack sizes and units…",
  "Cross-checking vendor catalogs…",
  "Untangling SKUs and pack descriptions…",
  "Asking the AI what '1 ea' really means…",
  "Teaching the model what a #10 can is…",
  "Sorting the parsley from the parsnips…",
  "Consulting the back-of-house oracle…",
  "Checking these aren't duplicates…",
  "Negotiating with the spreadsheet…",
  "Multi-vendor lists take a little longer—still crunching…",
  "Plating everything nicely for your review…",
];
const JAZZ_INTERVAL_MS = 2800;  // how often the line rotates
const JAZZ_QUIET_MS = 6000;     // how long a real status gets solo screen time

function formatFileEntry(entry) {
  const { fileName, importedCount, vendors } = entry;
  const n = importedCount;
  const itemWord = n === 1 ? 'item' : 'items';
  if (n === 0) {
    return `${fileName}: no items detected`;
  }
  if (!vendors || vendors.length === 0) {
    return `Added ${n} ${itemWord} from ${fileName}`;
  }
  if (vendors.length === 1) {
    return `Added ${n} ${itemWord} from ${vendors[0].name} (${fileName})`;
  }
  const parts = vendors.map(v => `${v.count} from ${v.name}`);
  return `${fileName}: added ${parts.join(', ')}`;
}

function buildVendorSentence(fileResults, totalItems) {
  const vendorTotals = {};
  (fileResults || []).forEach(entry => {
    const vendors = entry.vendors || [];
    if (vendors.length === 0) {
      const key = entry.fileName;
      vendorTotals[key] = (vendorTotals[key] || 0) + entry.importedCount;
    } else {
      vendors.forEach(v => {
        vendorTotals[v.name] = (vendorTotals[v.name] || 0) + v.count;
      });
    }
  });
  const sorted = Object.entries(vendorTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
  if (sorted.length === 0) return null;
  const MAX_VENDORS = 6;
  let displayed = sorted;
  let suffix = '';
  if (sorted.length > MAX_VENDORS) {
    displayed = sorted.slice(0, MAX_VENDORS);
    suffix = ` +${sorted.length - MAX_VENDORS} more`;
  }
  let vendorStr;
  if (displayed.length === 1) {
    vendorStr = displayed[0];
  } else {
    const last = displayed[displayed.length - 1];
    const rest = displayed.slice(0, -1);
    vendorStr = rest.join(', ') + ' and ' + last;
  }
  const itemWord = totalItems === 1 ? 'item' : 'items';
  return `Added a total of ${totalItems} ${itemWord} from ${vendorStr}${suffix}`;
}

export default function ImportProcessingModal({
  visible,
  classification,       // { type, large, cuisine, humorMessage }
  feedItems,            // [{ name, status }] — last 7 items being processed
  statusText,           // current phase text
  progressPct,          // 0–100
  itemsProcessed,
  totalEstimated,
  lastFileItemsCount,   // items imported in the final file — shown in the hold view
  fileResults,          // [{ fileName, importedCount, vendors }] — cumulative per-file results
  fileProgress,         // { currentIndex, totalFiles, currentFileName, completedCount } | null
  error,                // error message if failed
  completionSummary,    // { imported, errors, duplicates } — only set on error path
  readyForReview,       // true when processing succeeded and review table is ready
  sessionSummary,       // { totalItems, totalVendors, fileResults } | null — set after 5s auto-advance
  onDismiss,            // called when user closes after completion/error
  onNext,               // called when user clicks Next on the session summary
  // Order guide decision
  showOrderGuideWarning,
  onContinueAnyway,
  onSendToSupport,
}) {
  const feedRef = useRef(null);

  // Jazz rotation state. lastChangeAtRef tracks when the real status last
  // changed; the interval below just forces re-renders so the time-derived
  // rotation stays fresh. Everything else is computed at render time.
  const lastStatusRef = useRef(statusText);
  const lastChangeAtRef = useRef(Date.now());
  const [, forceJazzTick] = useState(0);
  if (statusText !== lastStatusRef.current) {
    lastStatusRef.current = statusText;
    lastChangeAtRef.current = Date.now();
  }
  const isProcessingView = !!visible
    && !showOrderGuideWarning && !sessionSummary && !readyForReview && !completionSummary;
  useEffect(() => {
    if (!isProcessingView) return undefined;
    const t = setInterval(() => forceJazzTick(n => n + 1), JAZZ_INTERVAL_MS);
    return () => clearInterval(t);
  }, [isProcessingView]);

  const quietMs = Date.now() - lastChangeAtRef.current;
  const statusStale = isProcessingView && quietMs > JAZZ_QUIET_MS;
  const displayStatus = statusStale
    ? JAZZ[Math.floor((quietMs - JAZZ_QUIET_MS) / JAZZ_INTERVAL_MS) % JAZZ.length]
    : (statusText || 'Extracting items…');

  // Auto-scroll the feed
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [feedItems]);

  useHideMobileNav(!!visible);

  if (!visible) return null;

  const humorMessage = classification?.humorMessage
    || CATEGORY_MESSAGES[classification?.cuisine]
    || CATEGORY_MESSAGES.food_general;

  const statusIcons = { parsing: '📄', normalizing: '🔧', categorizing: '🏷️', done: '✅', error: '❌' };

  const modalContent = (
    <div className="import-modal-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '.85rem 1rem .7rem', maxWidth: '520px',
        width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center', overflow: 'hidden', overflowX: 'hidden',
        maxHeight: 'min(760px, calc(90vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))', display: 'flex', flexDirection: 'column',
      }}>

        {/* ORDER GUIDE WARNING */}
        {showOrderGuideWarning ? (
          <div>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📋</div>
            <h3 style={{ color: '#5A4A42', fontWeight: 700, marginBottom: '0.5rem' }}>Large document detected</h3>
            <p style={{ color: '#7a6a62', fontSize: '0.95rem', lineHeight: 1.6 }}>
              This appears to be a large order guide or master product list.
              These typically contain far more items than a working inventory.
            </p>
            <p style={{ color: '#7a6a62', fontSize: '0.85rem' }}>
              We now automatically import up to 150 items, then queue support review for the remainder.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
              <button onClick={onContinueAnyway} style={{
                padding: '0.6rem 1.5rem', borderRadius: '8px', border: '1px solid #d4c8c0',
                background: '#fff', color: '#5A4A42', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
              }}>Continue with capped import</button>
              <button onClick={onSendToSupport} style={{
                padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none',
                background: '#5A4A42', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem',
              }}>Queue support review now</button>
            </div>
          </div>
        ) : sessionSummary ? (
          /* SESSION SUMMARY — shown after auto-advance from last file complete */
          <div>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📦</div>
            <h3 style={{ color: '#5A4A42', fontWeight: 700, marginBottom: '0.25rem' }}>Import session summary</h3>
            <p style={{ color: '#9a8a82', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Here's what was imported across all files
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'inline-block', margin: '0.5rem 1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#5A4A42', lineHeight: 1.1 }}>
                  {sessionSummary.totalItems}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#9a8a82', fontWeight: 500 }}>items added</div>
              </div>
              <div style={{ display: 'inline-block', margin: '0.5rem 1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#5A4A42', lineHeight: 1.1 }}>
                  {sessionSummary.totalVendors}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#9a8a82', fontWeight: 500 }}>vendors</div>
              </div>
            </div>
            {(() => {
              const sentence = buildVendorSentence(sessionSummary.fileResults, sessionSummary.totalItems);
              return sentence ? (
                <p style={{ color: '#7a6a62', fontSize: '0.85rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                  {sentence}
                </p>
              ) : null;
            })()}
            <button onClick={onNext} style={{
              padding: '0.5rem 2rem', borderRadius: '8px', border: 'none',
              background: '#5A4A42', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem',
            }}>Next</button>
          </div>
        ) : readyForReview ? (
          /* LAST FILE COMPLETE — cumulative per-file list, auto-advancing to session summary */
          <div>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
            <p style={{ color: '#5A4A42', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.75rem' }}>
              Import complete
            </p>
            {fileResults && fileResults.length > 0 ? (
              <div style={{ textAlign: 'left', marginBottom: '0.75rem' }}>
                {fileResults.map((entry, i) => {
                  const isLast = i === fileResults.length - 1;
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                      padding: '0.35rem 0',
                      borderBottom: i < fileResults.length - 1 ? '1px solid #f0ebe8' : 'none',
                    }}>
                      <span style={{ color: '#4caf50', fontSize: '1rem', flexShrink: 0 }}>✓</span>
                      <span style={{
                        fontSize: '0.88rem',
                        color: isLast ? '#5A4A42' : '#9a8a82',
                        fontWeight: isLast ? 700 : 400,
                        lineHeight: 1.4,
                      }}>
                        {formatFileEntry(entry)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (lastFileItemsCount > 0 || itemsProcessed > 0) && (
              <p style={{ color: '#7a6a62', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                <strong>{lastFileItemsCount > 0 ? lastFileItemsCount : itemsProcessed}</strong> items imported
              </p>
            )}
            <p style={{ color: '#9a8a82', fontSize: '0.85rem' }}>
              Preparing session summary…
            </p>
          </div>
        ) : completionSummary ? (
          /* ERROR COMPLETION */
          <div>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>❌</div>
            <h3 style={{ color: '#5A4A42', fontWeight: 700 }}>Import failed</h3>
            <p style={{ color: '#c44', fontSize: '0.9rem' }}>{error}</p>
            <button onClick={onDismiss} style={{
              marginTop: '1rem', padding: '0.5rem 2rem', borderRadius: '8px', border: 'none',
              background: '#5A4A42', color: '#fff', cursor: 'pointer', fontWeight: 600,
            }}>Close</button>
          </div>
        ) : (
          /* PROCESSING */
          <div>
            {/* Animated orb */}
            <div style={{ margin: '0 auto .3rem', width: '38px', height: '38px', minWidth: '38px', minHeight: '38px', flex: '0 0 38px', position: 'relative', flexShrink: 0 }}>
              <div className="ai-pulse-orb" style={{ width: '38px', height: '38px' }} />
            </div>
            <h3 style={{ color: '#5A4A42', fontWeight: 700, margin: '0 0 .15rem', fontSize: '1.02rem', lineHeight: 1.2 }}>Processing your import</h3>
            <p style={{ color: '#9a8a82', fontSize: '0.78rem', fontStyle: 'italic', margin: '0 0 .35rem', lineHeight: 1.3 }}>
              {humorMessage}
            </p>

            {/* Multi-file progress indicator */}
            {fileProgress && fileProgress.totalFiles > 1 && (
              <div style={{
                margin: '0 0 .35rem',
                background: '#faf8f6',
                border: '1px solid #efe9e4',
                borderRadius: '8px',
                padding: '0.4rem 0.6rem',
                textAlign: 'left',
                overflow: 'hidden', overflowX: 'hidden',
              }}>
                <div style={{ fontSize: '0.82rem', color: '#5A4A42', fontWeight: 600, marginBottom: '0.2rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Processing {fileProgress.currentFileName} — file {fileProgress.currentIndex} of {fileProgress.totalFiles}
                </div>
                {fileProgress.completedCount > 0 && (
                  <div style={{ fontSize: '0.78rem', color: '#9a8a82' }}>
                    {fileProgress.completedCount} of {fileProgress.totalFiles} files done
                  </div>
                )}
              </div>
            )}

            <p style={{ color: '#7a6a62', fontSize: '0.82rem', fontWeight: 600, margin: '0 0 .15rem', lineHeight: 1.3 }}>
              {displayStatus}
            </p>

            {/* Progress bar */}
            {totalEstimated > 0 && (
              <div style={{ margin: '.35rem 0', background: '#f0ebe8', borderRadius: '8px', height: '6px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(progressPct || 0, 100)}%`, height: '100%',
                  background: 'linear-gradient(90deg, #5A4A42, #8B7355)',
                  borderRadius: '8px', transition: 'width 0.5s ease',
                }} />
              </div>
            )}
            {itemsProcessed > 0 && (
              <p style={{ color: '#9a8a82', fontSize: '0.8rem' }}>
                {itemsProcessed}{totalEstimated ? ` of ~${totalEstimated}` : ''} items processed
              </p>
            )}

            {/* Live feed */}
            {feedItems && feedItems.length > 0 && (
              <div ref={feedRef} style={{
                marginTop: '.35rem', background: '#faf8f6', borderRadius: '8px',
                padding: '.3rem .5rem', maxHeight: '132px', overflowY: 'auto', overflowX: 'hidden',
                textAlign: 'left', border: '1px solid #efe9e4', minWidth: 0,
              }}>
                {feedItems.slice(-7).map((item, i) => (
                  <div key={i} style={{
                    padding: '0.2rem 0', borderBottom: i < feedItems.slice(-7).length - 1 ? '1px solid #f0ebe8' : 'none',
                    fontSize: '0.76rem', color: '#5A4A42', display: 'flex', gap: '0.4rem', alignItems: 'center', minWidth: 0, overflow: 'hidden',
                    opacity: i === feedItems.slice(-7).length - 1 ? 1 : 0.6 + (i * 0.05),
                  }}>
                    <span>{statusIcons[item.status] || '📄'}</span>
                    <span style={{ fontWeight: item.status === 'done' ? 400 : 600 }}>
                      {item.status === 'parsing' ? 'Parsing' : item.status === 'categorizing' ? 'Categorizing' : item.status === 'normalizing' ? 'Normalizing' : '✓'}:
                    </span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for the pulsing orb animation */}
      <style>{`
        .ai-pulse-orb {
          width: 38px; height: 38px; border-radius: 50%;
          background: url('/icons/counter_intel_button_chat_ci_badge.png') center/cover no-repeat;
          animation: orbPulse 2s ease-in-out infinite;
          box-shadow: 0 0 14px rgba(90,74,66,0.3); flex-shrink: 0;
        }
        @keyframes orbPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgba(90,74,66,0.3); }
          50% { transform: scale(1.08); box-shadow: 0 0 22px rgba(90,74,66,0.4); }
        }
      `}</style>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}
